import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PageTitle from '../common/PageTitle';
import DOMPurify from 'dompurify';
import { bookService } from '../../services/bookService';

const BookDetails = () => {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookDetails = async () => {
      try {
        const data = await bookService.getById(bookId);
        // Process book data
        setBook(data);
      } catch (error) {
        // Handle fetch error
      } finally {
        setLoading(false);
      }
    };

    fetchBookDetails();
  }, [bookId]);

  useEffect(() => {
    if (book) {
      // Book data loaded successfully
    }
  }, [book]);

  const handleBuyNow = () => { /* handle buy now logic */ };
  const handleAddToCart = () => { /* handle add to cart logic */ };

  const formatDate = (date) => {
    if (!date) return '';
    
    // استخدام Intl.DateTimeFormat لتنسيق التاريخ
    const formatter = new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });

    // تحويل الأرقام للعربية
    const arabicDate = formatter.format(new Date(date))
      .replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[d])
      .replace(/\//g, '‏/‏'); // إضافة علامات التشكيل حول الشرطة

    return arabicDate;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '٠ كيلوبايت';
    const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = Math.round(bytes / Math.pow(1024, i));
    // تحويل الرقم للعربية
    const arabicSize = String(size).replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
    return `${arabicSize} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            الكتاب غير موجود
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            عذراً، لم نتمكن من العثور على الكتاب المطلوب
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title={book.title} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Book Cover */}
          <div className="lg:col-span-1">
            <div className="max-w-[300px] mx-auto">
              <div className="aspect-w-3 aspect-h-4 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img
                  src={book.cover_url || "https://placehold.co/400x600"}
                  alt={book.title}
                  className="object-cover object-center w-full h-full"
                />
              </div>

              {/* Action Buttons */}
              <div className="mt-6 space-y-3 max-w-[300px] mx-auto">
                <button 
                  onClick={handleBuyNow}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  شراء الآن
                </button>
                <button
                  onClick={handleAddToCart}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-base font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  إضافة إلى السلة
                </button>
              </div>
            </div>
          </div>

          {/* Book Details */}
          <div className="mt-10 lg:mt-0 lg:col-span-2">
            <div className="space-y-8">
              {/* Basic Info */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {book.title}
                </h1>
                
                {/* Price */}
                <div className="mt-3 flex items-baseline">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">{book.price}</span>
                  <span className="mr-2 text-lg text-gray-500 dark:text-gray-400">ريال</span>
                  {book.old_price && (
                    <span className="mr-2 text-lg text-gray-500 line-through">
                      {book.old_price} ريال
                    </span>
                  )}
                </div>

                {/* Book Info */}
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <svg className="h-5 w-5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>المؤلف: {book.author}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <svg className="h-5 w-5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span>التصنيف: {book.category}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <svg className="h-5 w-5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>تاريخ النشر: {formatDate(book.created_at)}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <svg className="h-5 w-5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>حجم الملف: {formatFileSize(book.file_size)}</span>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-8">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    عن الكتاب
                  </h2>
                  <div 
                    className="prose prose-lg dark:prose-invert max-w-none"
                    style={{
                      '--tw-prose-body': 'var(--tw-prose-gray)',
                      '--tw-prose-headings': 'var(--tw-prose-gray)',
                      '--tw-prose-lead': 'var(--tw-prose-gray)',
                      '--tw-prose-links': 'var(--tw-prose-gray)',
                      '--tw-prose-bold': 'var(--tw-prose-gray)',
                      '--tw-prose-counters': 'var(--tw-prose-gray)',
                      '--tw-prose-bullets': 'var(--tw-prose-gray)',
                      '--tw-prose-hr': 'var(--tw-prose-gray)',
                      '--tw-prose-quotes': 'var(--tw-prose-gray)',
                      '--tw-prose-quote-borders': 'var(--tw-prose-gray)',
                      '--tw-prose-captions': 'var(--tw-prose-gray)',
                      '--tw-prose-code': 'var(--tw-prose-gray)',
                      '--tw-prose-pre-code': 'var(--tw-prose-gray)',
                      '--tw-prose-pre-bg': 'var(--tw-prose-gray)',
                      '--tw-prose-th-borders': 'var(--tw-prose-gray)',
                      '--tw-prose-td-borders': 'var(--tw-prose-gray)',
                    }}
                  >
                    <div 
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(book.description) }}
                      className="text-gray-600 dark:text-gray-300 leading-relaxed"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDetails;
